import { css } from '@emotion/core';
import { Theme } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';

// tslint:disable-next-line: no-import-side-effect
import '@fontsource-variable/figtree';

export const AGENT_FONT_STACK = '"Figtree Variable", "Figtree", "Roboto", "Helvetica", "Arial", sans-serif';

// Design tokens organized by purpose for easy maintenance
// Layout & Spacing
const LAYOUT = {
    densityMax: 12,
    densityMin: 4,
    headerBrandGap: 8,
    headerLogoSize: 32,
    introLogoMarginBottom: -16,
    introLogoMarginRight: -6,
    introLogoSize: 140,
    introLogoSizeSidebar: 100,
    runningToolMarginTop: 2
};

export const AGENT_LAYOUT = LAYOUT;

// Bubble/Message styling
const BUBBLE = {
    gapY: 8,
    maxWidthPct: 0.78,
    paddingX: 18,
    paddingY: 10,
    radius: 24
};

// Colors
const COLORS = {
    assistantText: '#1d1d1dff',
    composerBg: 'rgba(15, 23, 42, 0.02)',
    composerBorder: 'rgba(15, 23, 42, 0.02)',
    composerIcon: 'rgba(15, 23, 42, 0.6)',
    composerIconDisabled: 'rgba(15, 23, 42, 0.3)',
    headerBorder: 'rgba(15, 23, 42, 0.08)',
    offlineBannerBg: 'rgba(15, 23, 42, 0.04)',
    offlineBannerBorder: 'rgba(15, 23, 42, 0.08)',
    scrollbarThumb: 'rgba(15, 23, 42, 0.2)',
    toolBorder: 'rgba(0, 0, 0, 0.10)',
    toolCardBg: '#F9FAFB',
    toolCodeBg: '#F3F4F6',
    userBg: '#f2f2f2',
    userText: '#1d1d1dff'
};

export const AGENT_COLORS = COLORS;

// Composer
const COMPOSER = {
    baseShadow: '0px 4px 4px 0px #0000000a, 0px 0px 1px 0px #0000004a',
    dockShadow: '0 -12px 32px rgba(15, 23, 42, 0.08)',
    focusBorderAlpha: 0.2,
    heightMaxRows: 6,
    lineHeightPx: 28,
    radius: 32
};

// Thinking indicator (dots animation)
const THINKING_INDICATOR = {
    delayStepMs: 150,
    dotGap: 4,
    dotSize: 6,
    durationMs: 1200,
    opacityMax: 1,
    opacityMin: 0.5,
    scaleMin: 0.75
};

// Markdown styling
const MARKDOWN = {
    blockCodeBg: 'rgba(0, 0, 0, 0.05)',
    blockCodeBgUser: 'rgba(255, 255, 255, 0.2)',
    headingColor: '#0A0A0A',
    inlineCodeBg: 'rgba(0, 0, 0, 0.05)',
    inlineCodeBgUser: 'rgba(255, 255, 255, 0.2)'
};

// Sidebar
const SIDEBAR = {
    bg: '#f9f9f9',
    border: 'rgba(0, 0, 0, 0.08)',
    width: 260
};

export const sidebarStyles = (theme: Theme) => {
    // tslint:disable-next-line: no-magic-numbers
    const hoverBg = theme.palette.grey[50];
    // tslint:disable-next-line: no-magic-numbers
    const hoverBorder = theme.palette.grey[300];

    return css`
        width: ${SIDEBAR.width}px;
        flex-shrink: 0;
        white-space: nowrap;
        background-color: ${SIDEBAR.bg};
        border-right: 1px solid ${SIDEBAR.border};
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        height: 100vh;

        .app-bar-spacer {
            height: 64px;
        }

        .sidebar__header {
            display: flex;
            align-items: center;
            padding: ${theme.spacing(2)}px;
            justify-content: space-between;
        }

        .sidebar__newChatBtn {
            width: 100%;
            justify-content: flex-start;
            gap: 8px;
            padding: 8px 16px;
            text-transform: none;
            font-family: ${AGENT_FONT_STACK};
            background-color: ${theme.palette.background.paper};
            border: 1px solid ${COLORS.headerBorder};
            border-radius: 8px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

            &:hover {
                background-color: ${hoverBg};
                border-color: ${hoverBorder};
            }
        }

        .sidebar__list {
            padding: 8px;
        }

        /* Custom scrollbar */
        &::-webkit-scrollbar {
            width: 8px;
        }

        &::-webkit-scrollbar-thumb {
            background: ${COLORS.scrollbarThumb};
            border-radius: 999px;
        }

        .sidebar__listItem {
            border-radius: 8px;
            margin-bottom: 4px;
            padding-top: 10px;
            padding-bottom: 10px;

            &.Mui-selected {
                background-color: ${theme.palette.action.selected};
                &:hover {
                    background-color: ${theme.palette.action.selected};
                }
            }
        }

        .sidebar__itemText {
            margin: 0;

            .MuiTypography-body1 {
                font-size: 0.9rem;
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: ${AGENT_FONT_STACK};
                color: ${theme.palette.text.primary};
            }

            .MuiTypography-body2 {
                font-size: 0.75rem;
                font-family: ${AGENT_FONT_STACK};
                color: ${theme.palette.text.secondary};
                margin-top: 2px;
            }
        }

        .sidebar__loading {
            display: flex;
            flex-direction: column;
            padding: ${theme.spacing(2)}px;
            gap: 12px;
        }

        .sidebar__skeletonItem {
            display: flex;
            flex-direction: column;
            gap: 4px;
            padding: 8px 16px;
        }

        .sidebar__empty {
            padding: 24px 16px;
            text-align: center;
            color: ${theme.palette.text.secondary};
            font-size: 0.9rem;
            font-family: ${AGENT_FONT_STACK};
        }
    `;
};

// tslint:disable: no-magic-numbers
export const styles = (theme: Theme) => {
    const rowPadding = Math.max(LAYOUT.densityMin, Math.min(LAYOUT.densityMax, BUBBLE.gapY));
    const composerFocusBorder = fade(theme.palette.primary.main, COMPOSER.focusBorderAlpha);
    return css`
        & {
            flex: 1 1 auto;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            font-family: ${AGENT_FONT_STACK};
            font-optical-sizing: auto;
            background: ${theme.palette.background.default};
        }

        /* Override font for all MUI components */
        & .MuiTypography-root,
        & .MuiButton-root,
        & .MuiChip-root,
        & .MuiListItemText-primary,
        & .MuiListItemText-secondary,
        & .MuiInputBase-input,
        & .MuiFormLabel-root,
        & .MuiFormHelperText-root,
        & .MuiMenuItem-root,
        & .MuiTableCell-root,
        & .MuiTooltip-tooltip,
        & .MuiDialogTitle-root,
        & .MuiDialogContentText-root,
        & .MuiAlert-message,
        & .MuiSnackbarContent-message {
            font-family: ${AGENT_FONT_STACK};
        }

        /* Override body text size and line height */
        & .MuiTypography-body1,
        & .MuiTypography-body2 {
            font-size: 1rem;
            line-height: 1.6;
        }

        /* Agent-specific MUI component overrides */
        & .MuiChip-root {
            border-radius: 8px;
        }

        & .MuiChip-sizeSmall {
            height: 20px;
            text-transform: uppercase;
            letter-spacing: 0.3px;
            font-weight: 600;
            padding-left: 4px;
            padding-right: 4px;
        }

        & .MuiListItemText-secondary {
            margin-top: 4px;
        }

        .agent__body {
            flex: 1 1 auto;
            display: flex;
            flex-direction: column;
            padding: 24px;
            overflow-y: auto;
            min-height: 0;
        }

        .agent__content {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }

        .agent__content::after {
            content: '';
            display: block;
            height: 1px;
        }

        .agent__content table td,
        th {
            padding: 0 20px 5px 0;
        }

        .agent__body::-webkit-scrollbar {
            width: 8px;
        }

        .agent__body::-webkit-scrollbar-thumb {
            background: ${COLORS.scrollbarThumb};
            border-radius: 999px;
        }

        .agent__content--intro {
            align-items: stretch;
            text-align: center;
            justify-content: center;
            padding-bottom: 96px;
            flex: 1 1 auto;
        }

        .agent__intro {
            width: 100%;
            max-width: 640px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            gap: 12px;
            align-items: center;
        }

        .agent__intro--header {
            display: flex;
            align-items: flex-end;
            padding-right: 24px;
        }

        .agent__introLogo {
            width: ${LAYOUT.introLogoSize}px;
            height: ${LAYOUT.introLogoSize}px;
            object-fit: contain;
            transform: rotate(-10deg);
            transform-origin: center;
            margin-bottom: ${LAYOUT.introLogoMarginBottom}px;
            margin-right: ${LAYOUT.introLogoMarginRight}px;
        }

        &.agent__sidebar .agent__introLogo {
            width: ${LAYOUT.introLogoSizeSidebar}px;
            height: ${LAYOUT.introLogoSizeSidebar}px;
        }

        .agent__introHeading {
            font-weight: 600;
        }

        .agent__transcript {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .agent__offlineBanner {
            padding: 12px 16px;
            border-radius: 8px;
            background: ${COLORS.offlineBannerBg};
            border: 1px solid ${COLORS.offlineBannerBorder};
        }

        .agent__list {
            margin: 0;
            padding: 0;
        }

        .agent__list .MuiListItem-root {
            padding-top: ${rowPadding / 2}px;
            padding-bottom: ${rowPadding / 2}px;
        }

        .agent__row {
            padding-top: ${rowPadding / 2}px;
            padding-bottom: ${rowPadding / 2}px;
        }

        .agent__emailPreviewCard {
            margin-top: 8px;
            margin-bottom: 8px;
        }

        .agent__emailPreviewCardContent {
            display: flex;
            flex-direction: column;
            gap: 12px;
            background: #fcfcfa;

            .MuiButton-root {
                background: white;
            }
        }

        .agent__emailPreviewHeader {
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: space-between;
            border-bottom: 1px solid ${theme.palette.divider};
            margin-top: -8px;
            padding-bottom: 8px;

            .agent__emailPreviewIconButton {
                margin-top: -6px;
                margin-bottom: -6px;
            }
        }

        .agent__emailPreviewSubject {
            font-weight: 600;
        }

        .agent__emailEditor {
            .rte-toolbar {
                display: none;
            }

            .rte-editor-content {
                border: none;
                padding: 0;
                min-height: auto;
            }

            &.rte-editor.rte-is-disabled {
                .ProseMirror {
                    color: ${theme.palette.text.primary};
                }
            }
        }

        .agent__emailSendButtonContainer {
            display: flex;
            justify-content: flex-end;
            padding: 8px 0;
            border-top: 1px solid ${theme.palette.divider};
            margin-bottom: -16px;
            padding-top: 16px;
        }

        .agent__emailPreviewButton {
            align-self: flex-start;
        }

        .agent__loadingSkeletons {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 8px 0;
        }

        .agent__skeletonRow {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .agent__listText--user {
            text-align: right;
        }

        .agent__listText--assistant {
            text-align: left;
        }

        .agent__text {
            display: inline-block;
            white-space: normal;
            word-break: break-word;
            max-width: 100%;
            color: ${COLORS.assistantText};
        }

        .agent__secondary {
            color: ${COLORS.assistantText};
        }

        .agent__secondary--user {
            color: ${COLORS.userText};
        }

        .agent__mdList {
            margin: 10px 0;
            padding-left: 20px;
        }

        .agent__mdList--ordered {
            list-style: decimal;
        }

        .agent__mdList--unordered {
            list-style: disc;
        }

        .agent__mdListItem {
            margin: 0px 0 0;
        }

        .agent__mdListItem > ul,
        .agent__mdListItem > ol {
            margin-top: 0px;
        }

        .agent__mdParagraph {
            margin: 12px 0 0;
            white-space: pre-line;
        }

        .agent__mdSectionTitle {
            margin: 20px 0;
            font-weight: 600;
            color: ${MARKDOWN.headingColor};
            line-height: 1.35;
        }

        .agent__mdSectionTitle:first-child {
            margin-top: 0;
        }

        .agent__mdParagraph:first-child {
            margin-top: 0;
        }

        .agent__mdParagraph:last-child {
            margin-bottom: 0;
        }

        .agent__mdListItem .agent__mdParagraph {
            margin: 0;
        }

        .agent__mdListItem .agent__mdSectionTitle {
            margin: 0;
        }

        .agent__mdBreak {
            display: block;
            margin: 20px 0 0;
            line-height: 0;
        }

        .agent__mdHeading {
            margin: 16px 0 8px;
            font-weight: 600;
            color: ${MARKDOWN.headingColor};
            line-height: 1.35;
        }

        .agent__mdHeading:first-child {
            margin-top: 0;
        }

        .agent__mdHeading--h1,
        .agent__mdHeading--h2 {
            font-size: 1.05rem;
        }

        .agent__mdHeading--h3,
        .agent__mdHeading--h4 {
            font-size: 1rem;
        }

        .agent__mdHeading--h5,
        .agent__mdHeading--h6 {
            font-size: 0.95rem;
        }

        .agent__mdTableWrap {
            margin: 12px 0;
            max-width: 100%;
            border: 1px solid ${theme.palette.divider};
            border-radius: ${theme.shape.borderRadius}px;
            overflow: hidden;
        }

        .agent__mdTableScroll {
            max-width: 100%;
            overflow-x: auto;
            overflow-y: hidden;
            scrollbar-color: ${COLORS.scrollbarThumb} transparent;
            scrollbar-width: thin;
        }

        .agent__mdTableScroll::-webkit-scrollbar {
            height: 6px;
        }

        .agent__mdTableScroll::-webkit-scrollbar-track {
            background: transparent;
        }

        .agent__mdTableScroll::-webkit-scrollbar-thumb {
            background: ${COLORS.scrollbarThumb};
            border-radius: 999px;
        }

        .agent__text hr {
            border: none;
            border-top: 1px solid ${theme.palette.divider};
            margin: 14px 0;
        }

        .agent__mdTable {
            border-collapse: separate;
            border-spacing: 0;
            width: 100%;
        }

        .agent__mdTableWrap .agent__mdTable th,
        .agent__mdTableWrap .agent__mdTable td {
            border-right: 1px solid ${theme.palette.divider};
            border-bottom: 1px solid ${theme.palette.divider};
            padding: 8px 10px;
            text-align: left;
            vertical-align: top;
            white-space: normal;
            overflow-wrap: break-word;
            word-break: normal;
        }

        .agent__mdTableWrap .agent__mdTable tr > *:last-child {
            border-right: none;
        }

        .agent__mdTableWrap .agent__mdTable tbody tr:last-child > * {
            border-bottom: none;
        }

        .agent__mdTable thead th {
            background: ${theme.palette.action.hover};
            font-weight: 600;
            white-space: nowrap;
            overflow-wrap: normal;
            word-break: normal;
        }

        .agent__text code {
            font-family: monospace;
            font-size: 0.9em;
            background: ${MARKDOWN.inlineCodeBg};
            padding: 2px 4px;
            border-radius: 4px;
        }

        .agent__text--user code {
            background: ${MARKDOWN.inlineCodeBgUser};
        }

        .agent__text pre {
            margin: 8px 0;
            padding: 8px;
            background: ${MARKDOWN.blockCodeBg};
            border-radius: 8px;
            overflow-x: auto;
            font-family: monospace;
        }

        .agent__text pre code {
            padding: 0;
            background: transparent;
        }

        .agent__text--user pre {
            background: ${MARKDOWN.blockCodeBgUser};
        }

        .agent__text a {
            color: ${theme.palette.primary.main};
            text-decoration: underline;
        }

        .agent__text--user a {
            color: ${COLORS.userText};
        }

        .agent__assistantBlock {
            display: inline-block;
            max-width: ${BUBBLE.maxWidthPct * 100}%;
        }

        .agent__text--user {
            display: inline-block;
            white-space: pre-wrap;
            background: ${COLORS.userBg};
            color: ${COLORS.userText};
            padding: ${BUBBLE.paddingY}px ${BUBBLE.paddingX}px;
            border-radius: ${BUBBLE.radius}px;
            margin-left: auto;
            max-width: ${BUBBLE.maxWidthPct * 100}%;
            text-align: left;
        }

        .agent__mono {
            font-family: monospace;
        }

        .agent__eventRow {
            color: #555;
            font-family: monospace;
            font-size: 12px;
            margin-bottom: 6px;
            opacity: 0.9;
        }

        .agent__eventRow.is-error {
            color: #b71c1c;
        }

        .agent__feedItemError {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            box-sizing: border-box;
            width: 100%;
            padding: 12px 14px;
            color: ${theme.palette.error.dark};
            background: ${fade(theme.palette.error.main, 0.08)};
            border: 1px solid ${fade(theme.palette.error.main, 0.32)};
            border-radius: ${theme.shape.borderRadius}px;
            font-family: ${AGENT_FONT_STACK};
            font-size: 14px;
            line-height: 1.4;
        }

        .agent__feedItemErrorIcon {
            flex-shrink: 0;
            margin-top: 1px;
            font-size: 20px;
        }

        .agent__feedItemErrorTitle {
            margin-bottom: 2px;
            font-weight: 600;
        }

        .agent__feedItemErrorMessage {
            color: ${theme.palette.text.secondary};
        }

        .agent__toolRow {
            padding: 12px 14px;
            background: ${COLORS.toolCardBg};
            border: 1px solid ${COLORS.toolBorder};
            border-radius: ${theme.shape.borderRadius}px;
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .agent__toolRow.is-error {
            border-color: ${theme.palette.error.main}33;
        }

        .agent__toolSummary {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
        }

        .agent__toolSummaryMain {
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 0;
        }

        .agent__toolLabel {
            font-weight: 600;
            color: ${COLORS.assistantText};
        }

        .agent__toolSummaryMeta {
            display: flex;
            align-items: center;
            gap: 8px;
            color: ${theme.palette.text.secondary};
        }

        .agent__toolMeta {
            font-size: 12px;
            line-height: 1.4;
        }

        .agent__toolToggle {
            width: 28px;
            height: 28px;
            transition: transform 140ms ease;
            color: ${theme.palette.text.secondary};
        }

        .agent__toolToggle.is-expanded {
            transform: rotate(180deg);
        }

        .agent__toolSpinner {
            color: ${theme.palette.primary.main};
        }

        .agent__toolDetails {
            border-top: 1px solid ${COLORS.toolBorder};
            padding-top: 8px;
            margin-top: 4px;
        }

        .agent__toolDetailsText {
            margin: 0;
            font-family: monospace;
            font-size: 12px;
            white-space: pre-wrap;
            word-break: break-word;
            background: ${COLORS.toolCodeBg};
            border-radius: 6px;
            padding: 8px;
            color: ${theme.palette.text.secondary};
        }

        .agent__composer {
            margin-top: 12px;
            position: relative;
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        .agent__composer--intro {
            width: 100%;
        }

        .agent__composer--dock {
            width: 100%;
        }

        .agent__composer--dock {
            .agent__composerBar {
                margin-right: 45px;
            }
        }

        .agent__composerBar {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 16px;
            border-radius: ${COMPOSER.radius}px;
            border: 1px solid ${COLORS.composerBorder};
            background: ${COLORS.composerBg};
            box-shadow: ${COMPOSER.baseShadow};
            transition:
                border-color 120ms ease,
                box-shadow 120ms ease;
        }

        .agent__composerBar:focus-within {
            border-color: ${composerFocusBorder};
        }

        .agent__composerAttachment,
        .agent__composerAction {
            width: 36px;
            height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: ${COLORS.composerIcon};
        }

        .agent__composerAttachment.Mui-disabled,
        .agent__composerAction.Mui-disabled {
            color: ${COLORS.composerIconDisabled};
        }

        .agent__composerTextarea {
            flex: 1;
            border: none;
            resize: none;
            background: transparent;
            color: ${theme.palette.text.primary};
            font: inherit;
            line-height: ${COMPOSER.lineHeightPx}px;
            min-height: ${COMPOSER.lineHeightPx}px;
            max-height: ${COMPOSER.heightMaxRows * COMPOSER.lineHeightPx}px;
            padding: 0;
        }

        .agent__composerTextarea:focus {
            outline: none;
        }

        .agent__composerTextarea::placeholder {
            color: ${theme.palette.text.secondary};
        }

        .agent__composerAction--stop {
            position: relative;
        }

        .agent__composerSpinner {
            position: absolute;
            inset: 0;
            margin: auto;
            pointer-events: none;
        }

        .agent__composerControls {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 6px 8px 0;
        }

        .agent__composerModelTrigger {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 2px 6px;
            border: none;
            border-radius: 6px;
            background: transparent;
            font: inherit;
            font-size: 12px;
            color: ${theme.palette.text.secondary};
            cursor: pointer;
        }

        .agent__composerModelTrigger:hover {
            background: ${theme.palette.action.hover};
            color: ${theme.palette.text.primary};
        }

        .agent__composerModelTrigger:disabled {
            color: ${theme.palette.text.disabled};
            background: transparent;
            cursor: default;
        }

        .agent__composerModelTrigger .MuiSvgIcon-fontSizeSmall {
            font-size: 16px;
        }

        .agent__composerTriggerEffort {
            color: ${theme.palette.text.disabled};
        }

        .agent__composerStatus {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .agent__composerDock {
            position: sticky;
            bottom: 0;
            width: 100%;
            padding: 18px 24px 28px;
            background: ${theme.palette.background.paper};
            box-shadow: ${COMPOSER.dockShadow};
            z-index: 2;
            display: flex;
            justify-content: center;
        }

        .agent__composerDock .agent__composer {
            margin: 0 auto;
        }

        .agent__runningTool {
            color: ${theme.palette.text.secondary};
            margin-top: ${LAYOUT.runningToolMarginTop}px;
        }

        .agent__toolChips {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            margin-bottom: 2px;
        }
        .agent__toolChip .MuiChip-icon {
            margin-left: 4px;
        }
        .agent__toolIcon {
            font-size: 18px;
            vertical-align: middle;
        }
        .agent__toolIcon.is-error {
            color: #b71c1c;
        }
        .agent__toolIcon.is-success {
            color: ${theme.palette.primary.main};
        }

        /* thinking indicator */
        .agent__typing {
            display: inline-flex;
            align-items: center;
            gap: ${THINKING_INDICATOR.dotGap}px;
            color: ${COLORS.assistantText};
        }
        .agent__dot {
            width: ${THINKING_INDICATOR.dotSize}px;
            height: ${THINKING_INDICATOR.dotSize}px;
            border-radius: 50%;
            background: currentColor;
            opacity: ${THINKING_INDICATOR.opacityMin};
            animation: agentDotPulse ${THINKING_INDICATOR.durationMs}ms infinite ease-in-out;
        }
        .agent__dot:nth-of-type(2) {
            animation-delay: ${THINKING_INDICATOR.delayStepMs}ms;
        }
        .agent__dot:nth-of-type(3) {
            animation-delay: ${THINKING_INDICATOR.delayStepMs * 2}ms;
        }

        /* Thinking section */
        .agent__thinkingSection {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .agent__thinkingSummary {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .agent__thinkingLabel {
            font-size: 0.875rem;
            color: ${theme.palette.text.disabled};
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .agent__assistantBlock--wide {
            max-width: none !important;
        }

        @keyframes agentDotPulse {
            0%,
            80%,
            100% {
                transform: scale(${THINKING_INDICATOR.scaleMin});
                opacity: ${THINKING_INDICATOR.opacityMin};
            }
            40% {
                transform: scale(1);
                opacity: ${THINKING_INDICATOR.opacityMax};
            }
        }

        /* Composer pill/radius and accent (scoped) */
        & .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline {
            border-radius: ${COMPOSER.radius}px;
        }
    `;
};
// tslint:enable: no-magic-numbers

export const pageContentOverrides = css`
    .agent__content--intro {
        .agent__intro {
            min-width: 40%;
        }
    }

    &.agent--chat .agent__composerDock {
        box-shadow: none;
        max-width: 768px;
        margin: 0 auto;
        width: 100%;
        .agent__composer--dock .agent__composerBar {
            margin-right: 0;
        }
    }

    .agent__body {
        .agent__content {
            max-width: 768px;
            margin: 0 auto;
            width: 100%;
        }
    }
`;

export const pageStyles = css`
    flex: 1 1 auto;
    display: flex;
    overflow: hidden;
`;

export const agentHeaderStyles = (theme: Theme) => css`
    padding: 16px 24px;
    border-bottom: 1px solid ${COLORS.headerBorder};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    font-family: ${AGENT_FONT_STACK};

    /* Override font for all MUI components in header */
    & .MuiTypography-root,
    & .MuiButton-root,
    & .MuiChip-root {
        font-family: ${AGENT_FONT_STACK};
    }

    /* Agent-specific MUI component overrides for header */
    & .MuiChip-root {
        border-radius: 8px;
    }

    & .MuiChip-sizeSmall {
        height: 20px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
        font-weight: 600;
        padding-left: 4px;
        padding-right: 4px;
    }

    .agent__headerBrand {
        display: inline-flex;
        align-items: center;
        gap: ${LAYOUT.headerBrandGap}px;
    }

    .agent__headerActions {
        display: inline-flex;
        align-items: center;
        gap: 8px;
    }

    &.agent-page-header-actions {
        /* Ensure chips (Genius/Guided) use primary color */
        .MuiChip-root {
            color: white;
            background: ${theme.palette.primary.main};
        }
    }

    .agent__headerActions .MuiButton-root {
        color: ${theme.palette.action.active};
        text-transform: none;
    }

    .agent__headerActions .MuiIconButton-root {
        color: ${theme.palette.action.active};
    }

    .agent__logo {
        width: ${LAYOUT.headerLogoSize}px;
        height: ${LAYOUT.headerLogoSize}px;
        object-fit: contain;
        transform: rotate(-10deg);
        transform-origin: center;
    }

    .agent__logo.is-hidden {
        display: none;
    }

    .agent__headerTitle {
        font-weight: 600;
    }
`;

export const agentLayoutStyles = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: hidden;
    font-family: ${AGENT_FONT_STACK};

    .agent-layout__body {
        flex: 1 1 auto;
        display: flex;
        flex-direction: row;
        overflow: hidden;
        min-height: 0;
    }

    .agent-layout__content {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        flex-direction: column;
    }
`;

// Conversation dropdown styles
export const conversationDropdownStyles = (theme: Theme) => css`
    .conversation-dropdown__trigger {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 8px;
        margin: -4px -8px;
        border: none;
        background: transparent;
        cursor: pointer;
        border-radius: 6px;
        font-family: ${AGENT_FONT_STACK};
        font-size: 1rem;
        font-weight: 600;
        color: ${theme.palette.text.primary};
        max-width: 200px;
        transition: background-color 120ms ease;

        &:hover {
            background-color: ${theme.palette.action.hover};
        }
    }

    .conversation-dropdown__triggerText {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .conversation-dropdown__chevron {
        flex-shrink: 0;
        font-size: 20px;
        color: ${theme.palette.text.secondary};
        transition: transform 200ms ease;
    }

    .conversation-dropdown__chevron.is-open {
        transform: rotate(180deg);
    }
`;

export const conversationDropdownMenuStyles = (theme: Theme) => css`
    .conversation-dropdown__sectionHeader {
        padding: 12px 16px 6px;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: ${theme.palette.text.secondary};
        font-family: ${AGENT_FONT_STACK};
        line-height: 1.2;
        min-height: auto;
    }

    .conversation-dropdown__item {
        padding: 10px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;

        &.Mui-selected {
            background-color: ${theme.palette.action.selected};
        }

        &:hover {
            background-color: ${theme.palette.action.hover};
        }
    }

    .conversation-dropdown__itemText {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: ${AGENT_FONT_STACK};
        font-size: 0.9rem;
    }

    .conversation-dropdown__checkIcon {
        flex-shrink: 0;
        font-size: 18px;
        color: ${theme.palette.primary.main};
    }

    .conversation-dropdown__empty {
        padding: 24px 16px;
        text-align: center;
        color: ${theme.palette.text.secondary};
        font-size: 0.9rem;
        font-family: ${AGENT_FONT_STACK};
    }

    .conversation-dropdown__divider {
        margin: 8px 0;
    }

    .conversation-dropdown__viewAllLink {
        display: block;
        padding: 10px 16px;
        font-size: 0.9rem;
        font-family: ${AGENT_FONT_STACK};
        color: ${theme.palette.primary.main};
        text-decoration: none;

        &:hover {
            background-color: ${theme.palette.action.hover};
        }
    }
`;
